import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Pencil,
  Trash2,
  FolderKanban,
  Calendar,
  ArrowRight,
} from "lucide-react";

/* ===================================================== */
/* 🧱 Status Badge                                       */
/* ===================================================== */
function StatusBadge({ status = "active" }) {
  const s = String(status).toLowerCase();

  const styles = {
    active: "bg-emerald-50 text-emerald-700 border-emerald-200",
    paused: "bg-amber-50 text-amber-700 border-amber-200",
    completed: "bg-blue-50 text-blue-700 border-blue-200",
    archived: "bg-gray-100 text-gray-600 border-gray-200",
    default: "bg-gray-50 text-gray-600 border-gray-200",
  };

  const cls = styles[s] || styles.default;

  const label =
    s && typeof s === "string"
      ? s.charAt(0).toUpperCase() + s.slice(1)
      : "Active";

  return (
    <span
      className={`text-xs font-medium border px-2 py-0.5 rounded-md ${cls}`}
    >
      {label}
    </span>
  );
}

/* ===================================================== */
/* 🧱 Project Card                                       */
/* ===================================================== */
export default function ProjectCard({
  project = {},
  href,
  onOpen,
  onEdit,
  onDelete,
  className = "",
}) {
  const navigate = useNavigate();

  /* ---------------- SAFE DATA ---------------- */
  const id = project?._id || "";
  const title = project?.name || "Untitled Project";
  const desc = project?.description || "";
  const status = project?.status || "active";
  const progress = Number(project?.progress || 0);

  const updated = project?.updatedAt
    ? new Date(project.updatedAt)
    : project?.createdAt
    ? new Date(project.createdAt)
    : null;

  const start = project?.startDate
    ? new Date(project.startDate)
    : null;

  const end = project?.endDate
    ? new Date(project.endDate)
    : null;

  /* ===================================================== */
  /* 🔗 NAVIGATION                                         */
  /* ===================================================== */

const openProject = useCallback(() => {
  if (!project?._id) {
    console.warn("[ProjectCard] Missing project ID", project);
    return;
  }

  navigate(`/projects/${project._id}`);
}, [project, navigate]);

  /* ===================================================== */
  /* 🛑 PREVENT EVENT BUBBLING (CRITICAL FIX)              */
  /* ===================================================== */

  const handleEdit = (e) => {
    e.stopPropagation();
    onEdit?.(project);
  };

  const handleDelete = (e) => {
    e.stopPropagation();
    onDelete?.(project);
  };

  /* ===================================================== */
  /* 🎨 PROGRESS COLOR                                    */
  /* ===================================================== */

  const progressColor =
    progress >= 100
      ? "bg-blue-600"
      : progress >= 50
      ? "bg-indigo-500"
      : "bg-amber-500";

  /* ===================================================== */
  /* 🖥️ RENDER                                            */
  /* ===================================================== */

  return (
    <div
      onClick={openProject}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") openProject();
      }}
      className={`group cursor-pointer relative rounded-2xl border border-gray-200 bg-white p-5 shadow-sm hover:shadow-md transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500 ${className}`}
    >
      {/* ================= HEADER ================= */}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-semibold text-gray-900 truncate">
              {title}
            </span>

            {id && (
              <span className="text-xs font-mono text-gray-400">
                #{String(id).slice(-6)}
              </span>
            )}

            <StatusBadge status={status} />
          </div>

          {desc && (
            <p className="mt-1 text-sm text-gray-600 line-clamp-2">
              {desc}
            </p>
          )}

          {updated && (
            <div className="mt-1 text-xs text-gray-400">
              Updated {updated.toLocaleDateString()}
            </div>
          )}
        </div>

        {/* ACTIONS */}
        <div className="flex shrink-0 items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
          {onEdit && (
            <button
              onClick={handleEdit}
              className="p-1.5 rounded-md border text-gray-600 hover:bg-gray-50"
              title="Edit project"
            >
              <Pencil className="w-4 h-4" />
            </button>
          )}

          {onDelete && (
            <button
              onClick={handleDelete}
              className="p-1.5 rounded-md border text-red-500 hover:bg-red-50 border-red-200"
              title="Delete project"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      {/* ================= PROGRESS ================= */}
      <div className="mt-4">
        <div className="flex justify-between text-xs text-gray-500 mb-1">
          <span>Progress</span>
          <span>{progress}%</span>
        </div>

        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
          <div
            className={`h-full rounded-full transition-all duration-300 ${progressColor}`}
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
      </div>

      {/* ================= TIMELINE ================= */}
      {(start || end) && (
        <div className="mt-3 flex items-center gap-3 text-xs text-gray-500">
          <Calendar className="w-3.5 h-3.5 text-gray-400" />

          {start && <span>{start.toLocaleDateString()}</span>}

          {end && (
            <>
              <ArrowRight className="w-3 h-3 text-gray-400" />
              <span>{end.toLocaleDateString()}</span>
            </>
          )}
        </div>
      )}

      {/* ================= FOOTER ================= */}
      <div className="mt-4 flex justify-between items-center">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <FolderKanban className="w-3.5 h-3.5" />
          <span>
            {status === "completed" ? "Completed" : "In progress"}
          </span>
        </div>

        <span className="text-sm font-medium text-indigo-600 group-hover:underline">
          View Details →
        </span>
      </div>
    </div>
  );
}